import { Drawer, createStyles } from "@mantine/core";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getAllPatients } from "../../services/patient";
import { getAllBillingModal } from "../../services/billing";
import { LuChevronRight, LuSearch } from "react-icons/lu";
import CustomBillingPagination from "../Pagination/CustomBillingPagination";
import { decrypt } from "../../utils/index";
import moment from "moment";

const INK = "#171717";
const INK2 = "#262626";
const MUTED = "#667085";
const LINE = "#E5E5E5";
const LINE_SOFT = "#F1F1F1";
const SURFACE = "#FAFAFA";
const ACCENT = "#FFBA45";
const ACCENT_SOFT = "#FFF4DA";
const ACCENT_BORDER = "#F5C518";
const ACCENT_DEEP = "#B7791F";
const MONO = "ui-monospace, 'SFMono-Regular', Menlo, monospace";

const num = (v) => parseFloat(v) || 0;
const findClientByName = (c, pats) => {
  const fn = (c?.pat_name_f || "").toLowerCase();
  const ln = (c?.pat_name_l || "").toLowerCase();
  if (fn && ln) {
    const byBoth = pats.find(
      (p) => p?.form1?.firstName?.toLowerCase() === fn && p?.form1?.lastName?.toLowerCase() === ln,
    );
    if (byBoth) return byBoth;
  }
  if (fn) {
    const byFirst = pats.find((p) => p?.form1?.firstName?.toLowerCase() === fn);
    if (byFirst) return byFirst;
  }
  if (ln) {
    const byLast = pats.find((p) => p?.form1?.lastName?.toLowerCase() === ln);
    if (byLast) return byLast;
  }
  return null;
};

const getClientImage = (c, allPatients) => {
  try {
    const matched = findClientByName(c, allPatients || []);
    const raw = matched?.imageurl;
    if (!raw) return null;
    return decrypt(raw) || null;
  } catch {
    return null;
  }
};
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");
const claimTotal = (c) =>
  c?.total_charge != null && c?.total_charge !== ""
    ? num(c.total_charge)
    : (c?.charge || []).reduce((t, ch) => t + num(ch?.charge), 0);
const patName = (c) =>
  [c?.pat_name_f, c?.pat_name_l].filter(Boolean).join(" ") || "—";
const getInitials = (name) =>
  name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
const statusLabel = (s) =>
  s === "P"
    ? "Paid"
    : s === "R"
      ? "Rejected"
      : s === "A"
        ? "Acknowledged"
        : "Pending";

const useStyles = createStyles(() => ({
  shell: {
    width: "50vw",
    maxWidth: 640,
    minWidth: 540,
    height: "calc(100vh - 32px)",
    margin: "16px 16px 16px auto",
    background: "#fff",
    borderRadius: 16,
    border: `1px solid ${LINE}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    flexShrink: 0,
  },

  /* head */
  head: {
    padding: "22px 28px",
    borderBottom: `1px solid ${LINE_SOFT}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexShrink: 0,
  },
  eyebrow: {
    margin: "0 0 6px",
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: MUTED,
    fontFamily: "Inter",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: INK,
    letterSpacing: "-0.018em",
    fontFamily: "Inter",
  },
  closeBtn: {
    width: 36,
    height: 36,
    background: "#F4F4F5",
    border: 0,
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: INK2,
    flexShrink: 0,
    "& svg": { width: 18, height: 18 },
    "&:hover": { background: "#E4E4E7" },
  },

  /* body */
  body: {
    flex: 1,
    overflowY: "auto",
    padding: 0,
  },
  bodyInner: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100%",
    padding: "22px 24px",
  },
  confirmScroll: {
    padding: "22px 24px",
  },

  /* search */
  searchWrap: {
    position: "relative",
    marginBottom: 12,
    flexShrink: 0,
    "& input": {
      width: "100%",
      height: 40,
      padding: "0 14px 0 38px",
      border: `1px solid ${LINE}`,
      background: "#fff",
      borderRadius: 8,
      fontFamily: "Inter",
      fontSize: 13,
      color: INK,
      outline: "none",
      "&:focus": { borderColor: INK },
      "&::placeholder": { color: "#9CA3AF" },
    },
    "& svg": {
      position: "absolute",
      left: 12,
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 16,
      color: MUTED,
      pointerEvents: "none",
    },
  },

  /* prev-claim-list */
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    flex: 1,
    overflowY: "auto",
    padding: "2px",
  },

  /* batch */
  batch: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${LINE}`,
    borderRadius: 10,
    background: "#fff",
    overflow: "hidden",
  },
  batchRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    padding: "12px 14px",
    border: 0,
    background: "#fff",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    transition: "background .12s ease",
    "&:hover": { background: SURFACE },
  },
  batchRowOpen: { borderBottom: `1px solid ${LINE}` },
  batchRowSelected: {
    background: ACCENT_SOFT,
    boxShadow: `inset 3px 0 0 ${ACCENT}`,
    "&:hover": { background: ACCENT_SOFT },
  },
  chevBtn: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    borderRadius: 6,
    display: "grid",
    placeItems: "center",
    color: MUTED,
    cursor: "pointer",
    flexShrink: 0,
    transition: "background .12s ease, transform .15s ease",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: "rgba(0,0,0,0.06)" },
  },
  chevOpen: { transform: "rotate(90deg)" },

  batchInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  batchR1: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 13,
    fontFamily: MONO,
    color: INK,
  },
  batchR1Left: { display: "flex", alignItems: "center", gap: 10 },
  batchMeta: {
    fontFamily: "Inter",
    fontSize: 11.5,
    fontWeight: 600,
    color: MUTED,
    background: "#F5F5F5",
    padding: "3px 7px",
    borderRadius: 999,
  },
  batchR2: {
    fontSize: 12.5,
    color: INK2,
    fontWeight: 500,
    fontFamily: "Inter",
  },
  batchR3: { fontSize: 11.5, color: MUTED, fontFamily: "Inter" },

  children: { display: "flex", flexDirection: "column", background: "#fff" },

  /* child claim row */
  childRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "12px 14px 12px 42px",
    border: 0,
    borderTop: `1px solid ${LINE}`,
    borderRadius: 0,
    background: "#fff",
    fontFamily: "inherit",
    textAlign: "left",
    cursor: "pointer",
    fontSize: 12.5,
    transition: "background .12s ease",
    "&:hover": { background: SURFACE, borderColor: LINE },
  },
  childRowSelected: {
    background: ACCENT_SOFT,
    borderColor: LINE,
    boxShadow: `inset 3px 0 0 ${ACCENT}`,
  },

  av: {
    width: 32,
    height: 32,
    borderRadius: 999,
    background: `${ACCENT_SOFT} center/cover no-repeat`,
    color: ACCENT_DEEP,
    fontSize: 11,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    overflow: "hidden",
    fontFamily: "Inter",
  },
  avImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    borderRadius: 999,
  },
  childInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    flex: 1,
    minWidth: 0,
  },
  childR1: { fontSize: 12, fontFamily: MONO, color: INK, fontWeight: 700 },
  childR2: { fontSize: 13, fontWeight: 600, color: INK, fontFamily: "Inter" },
  childFields: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "4px 16px",
    marginTop: 4,
  },
  fieldK: {
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: MUTED,
    fontFamily: "Inter",
  },
  fieldV: { fontSize: 11.5, color: INK2, fontWeight: 500, fontFamily: "Inter" },

  emptyText: {
    textAlign: "center",
    padding: "32px 16px",
    color: MUTED,
    fontFamily: "Inter",
    fontSize: 13,
  },

  /* confirm pane */
  confirmDesc: {
    margin: "0 0 14px",
    fontSize: 13.5,
    color: MUTED,
    lineHeight: 1.55,
    fontFamily: "Inter",
  },
  confirmCard: {
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    padding: "16px 18px",
    background: SURFACE,
  },
  confirmCardTop: { display: "flex", gap: 14, alignItems: "center" },
  confirmCardAv: {
    width: 44,
    height: 44,
    borderRadius: 999,
    background: ACCENT_SOFT,
    color: ACCENT_DEEP,
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    fontFamily: "Inter",
  },
  confirmCardAvImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    borderRadius: 999,
  },
  confirmCardMid: { flex: 1, minWidth: 0 },
  confirmCardRight: { textAlign: "right" },
  confirmCardName: {
    fontSize: 14.5,
    fontWeight: 700,
    color: INK,
    fontFamily: "Inter",
  },
  confirmCardMeta: {
    fontSize: 12.5,
    color: MUTED,
    marginTop: 2,
    fontFamily: "Inter",
  },
  confirmCardId: { fontFamily: MONO, fontSize: 11.5, color: MUTED },
  confirmCardAmt: {
    fontSize: 14,
    fontWeight: 700,
    color: INK,
    marginTop: 2,
    fontFamily: "Inter",
  },
  confirmGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px 24px",
    marginTop: 16,
    paddingTop: 14,
    borderTop: `1px solid ${LINE}`,
  },
  confirmGridK: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: MUTED,
    fontFamily: "Inter",
  },
  confirmGridV: {
    fontSize: 13,
    color: INK,
    fontWeight: 500,
    marginTop: 2,
    fontFamily: "Inter",
  },

  confirmDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 18,
    marginTop: 18,
  },
  confirmSectionH6: {
    margin: "0 0 8px",
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: MUTED,
    fontFamily: "Inter",
  },
  confirmKv: {
    margin: 0,
    display: "grid",
    gridTemplateColumns: "120px 1fr",
    rowGap: 6,
    columnGap: 14,
    fontSize: 13,
  },
  confirmKvDt: { color: MUTED, fontWeight: 500, fontFamily: "Inter" },
  confirmKvDd: { margin: 0, color: INK, fontWeight: 500, fontFamily: "Inter" },
  diagText: {
    margin: 0,
    fontSize: 13,
    color: INK,
    fontWeight: 500,
    fontFamily: MONO,
  },
  monoCell: { fontFamily: MONO, fontWeight: 600 },

  svcTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
    marginBottom: 10,
    "& thead th": {
      textAlign: "left",
      padding: "8px 10px",
      background: SURFACE,
      borderBottom: `1px solid ${LINE}`,
      fontSize: 10.5,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: MUTED,
    },
    "& thead th.num, & tbody td.num": {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
    },
    "& tbody td": {
      padding: "9px 10px",
      borderBottom: `1px solid ${LINE_SOFT}`,
      color: INK,
      fontWeight: 500,
      verticalAlign: "top",
    },
    "& tbody tr:last-child td": { borderBottom: 0 },
  },
  svcTotals: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 12px",
    background: SURFACE,
    borderRadius: 8,
    fontSize: 12.5,
  },
  svcTotalsK: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: MUTED,
    display: "block",
    fontFamily: "Inter",
  },
  svcTotalsV: {
    fontWeight: 700,
    color: INK,
    fontFamily: MONO,
    fontSize: 14,
    marginTop: 2,
    display: "block",
  },

  /* foot */
  foot: {
    padding: "16px 28px 22px",
    borderTop: `1px solid ${LINE_SOFT}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    flexShrink: 0,
  },
  ghostBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: `1px solid ${LINE}`,
    background: "transparent",
    color: INK2,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    "&:hover": { background: "#F4F4F5" },
  },
  accentBtn: {
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: 0,
    background: ACCENT,
    color: INK,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter",
    "&:hover": { background: "#EEAA33" },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
  },
}));

const UsePreviousClaimDrawer = ({ opened, onClose, onSelect }) => {
  const { classes, cx } = useStyles();
  const { allBilling } = useSelector((state) => state?.billing);
  const { patients } = useSelector((state) => state?.patient);

  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [openBatches, setOpenBatches] = useState({});
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);

  // Patients fetch — only when drawer opens
  useEffect(() => {
    if (opened) getAllPatients("all", true, true);
  }, [opened]);

  // Billing fetch — single effect covering page + search changes
  useEffect(() => {
    if (!opened) return;
    getAllBillingModal({ page: currentPage, name: search || undefined, status: 'notRejected' });
  }, [opened, currentPage, search]);

  // Reset open batch rows when page changes so stale rows don't stay open
  useEffect(() => {
    setOpenBatches({});
  }, [currentPage]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setCurrentPage(1); // batched with setSearch — single effect fires once
  };

  const billingList = allBilling?.Billing || [];

  const decryptBatch = (item) => {
    try {
      const raw = item?.billingForm;
      if (typeof raw === "string") return JSON.parse(decrypt(raw, false));
      return raw;
    } catch {
      return item?.billingForm;
    }
  };

  const getAuthExpiry = (c) => {
    const allPats = patients?.patients || [];
    const insNumber = c?.ins_number || c?.pcn;
    if (!insNumber) return "—";
    const matched = allPats.find(
      (p) =>
        p?.form2?.BillingInsurance?.[0]?.identificationNumber === insNumber,
    );
    const endDate = matched?.form2?.BillingInsurance?.[0]?.authorizationEndDate;
    return endDate ? fmtDate(endDate) : "—";
  };

  const toggleBatch = (id) => setOpenBatches((p) => ({ ...p, [id]: !p[id] }));

  const handleClose = () => {
    setSearch("");
    setCurrentPage(1);
    setOpenBatches({});
    setSelectedClaim(null);
    setSelectedBatch(null);
    onClose();
    // Restore main billing table — drawer used notRejected filter which overwrote shared Redux state
    getAllBillingModal({ page: 1 });
  };

  const handleContinue = () => {
    if (selectedClaim) {
      onSelect?.(selectedClaim, selectedBatch);
      handleClose();
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      size="100%"
      padding={0}
      withCloseButton={false}
      overlayProps={{ zIndex: 1001 }}
      zIndex={1002}
      transitionDuration={180}
      styles={{
        content: {
          background: "transparent",
          boxShadow: "none",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        header: { display: "none" },
        body: {
          padding: 0,
          flex: 1,
          overflow: "hidden",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end",
          height: "100%",
        },
      }}
    >
      <div className={classes.shell}>
        {/* head */}
        <div className={classes.head}>
          <div>
            <p className={classes.eyebrow}>Use a Previous Claim</p>
            <h2 className={classes.title}>Pick a previous claim</h2>
          </div>
          <button
            type="button"
            className={classes.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* body */}
        <div className={classes.body}>
          <div className={classes.bodyInner}>
              <div className={classes.searchWrap}>
                <LuSearch />
                <input
                  type="text"
                  placeholder="Search past claims by ID, client, or payer"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
              </div>

              <div className={classes.list}>
                {billingList.length === 0 && (
                  <p className={classes.emptyText}>No previous claims found.</p>
                )}
                {billingList.map((item) => {
                  const bf = decryptBatch(item);
                  const claims = bf?.claim || [];
                  const batchId = item?._id || "—";
                  const displayBatchId = item?.batchId || `batch-${(item?._id || "").toString().slice(-6)}`;
                  const payerName = bf?.claim?.[0]?.payer_name || "—";
                  const providerName = bf?.claim?.[0]?.bill_name || "—";
                  const submittedDate = fmtDate(item?.createdAt);
                  const isOpen = !!openBatches[batchId];

                  return (
                    <div key={batchId} className={classes.batch}>
                      <button
                        type="button"
                        className={cx(
                          classes.batchRow,
                          isOpen && classes.batchRowOpen,
                          selectedClaim?.isBatch && selectedClaim?.batchId === displayBatchId && classes.batchRowSelected,
                        )}
                        onClick={() => {
                          setSelectedClaim({
                            isBatch: true,
                            batchId: displayBatchId,
                            claims,
                            prov_npi: claims[0]?.prov_npi || "",
                            prov_id: claims[0]?.prov_id || "",
                            bill_npi: claims[0]?.bill_npi || "",
                            bill_id: claims[0]?.bill_id || "",
                            bill_taxid: claims[0]?.bill_taxid || "",
                            bill_name: claims[0]?.bill_name || "",
                            payerid: claims[0]?.payerid || "",
                            payer_name: claims[0]?.payer_name || "",
                            payer_order: claims[0]?.payer_order || "",
                            charge: claims.flatMap((c) => c?.charge || []),
                          });
                          setSelectedBatch(item);
                          if (!isOpen) toggleBatch(batchId);
                        }}
                      >
                        <span
                          className={cx(
                            classes.chevBtn,
                            isOpen && classes.chevOpen,
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleBatch(batchId);
                          }}
                        >
                          <LuChevronRight size={14} />
                        </span>
                        <span className={classes.batchInfo}>
                          <span className={classes.batchR1}>
                            <b>{displayBatchId}</b>
                            <span className={classes.batchMeta}>
                              {claims.length} claim
                              {claims.length !== 1 ? "s" : ""}
                            </span>
                          </span>
                          <span className={classes.batchR2}>
                            {payerName} · {providerName}
                          </span>
                          <span className={classes.batchR3}>
                            Submitted {submittedDate}
                          </span>
                        </span>
                      </button>

                      {isOpen && (
                        <div className={classes.children}>
                          {claims.map((c, ci) => {
                            const name = patName(c);
                            const isSelected = selectedClaim?.claimmd_id
                              ? selectedClaim.claimmd_id === c.claimmd_id
                              : selectedClaim === c;
                            return (
                              <button
                                key={ci}
                                type="button"
                                className={cx(
                                  classes.childRow,
                                  isSelected && classes.childRowSelected,
                                )}
                                onClick={() => {
                                  setSelectedClaim(c);
                                  setSelectedBatch(item);
                                }}
                              >
                                <span className={classes.av}>
                                  {getClientImage(c, patients?.patients)
                                    ? <img src={getClientImage(c, patients?.patients)} alt={name} className={classes.avImg} />
                                    : getInitials(name)
                                  }
                                </span>
                                <span className={classes.childInfo}>
                                  <span className={classes.childR1}>
                                    {c?.claimmd_id || `CLM-${ci + 1}`}
                                  </span>
                                  <span className={classes.childR2}>
                                    {name}
                                  </span>
                                  <span className={classes.childFields}>
                                    <span>
                                      <span className={classes.fieldK}>
                                        DOB
                                      </span>
                                      <div className={classes.fieldV}>
                                        {c?.pat_dob ? fmtDate(c.pat_dob) : "—"}
                                      </div>
                                    </span>
                                    <span>
                                      <span className={classes.fieldK}>
                                        Auth ID
                                      </span>
                                      <div className={classes.fieldV}>
                                        {c?.prior_auth || "—"}
                                      </div>
                                    </span>
                                    <span>
                                      <span className={classes.fieldK}>
                                        Auth Expires
                                      </span>
                                      <div className={classes.fieldV}>
                                        {getAuthExpiry(c)}
                                      </div>
                                    </span>
                                  </span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {allBilling?.count > 0 && (
                <CustomBillingPagination
                  data={allBilling.count}
                  itemsPerPage={10}
                  currentPage={currentPage}
                  handlePageChange={(p) => setCurrentPage(p)}
                />
              )}
            </div>
        </div>

        {false &&
            selectedClaim &&
            (() => {
              const bf = selectedBatch ? decryptBatch(selectedBatch) : null;
              const payerName =
                selectedClaim?.payer_name || bf?.claim?.[0]?.payer_name || "—";
              const providerName =
                selectedClaim?.bill_name ||
                bf?.claim?.[0]?.bill_name ||
                "—";
              const lines = selectedClaim?.charge || [];
              const serviceFrom = lines.find((l) => l?.from_date)?.from_date;
              const serviceTo = lines.find((l) => l?.thru_date)?.thru_date;

              if (selectedClaim.isBatch) {
                const batchClaims = selectedClaim.claims || [];
                const batchTotal = lines.reduce((t, ch) => t + num(ch?.charge), 0);
                return (
                  <div className={classes.confirmScroll}>
                    <p className={classes.confirmDesc}>
                      You&apos;re about to create {batchClaims.length} new claim
                      {batchClaims.length !== 1 ? "s" : ""} by duplicating the
                      selected batch. Each client will get a new claim using the
                      same provider, payer, and service codes. Today&apos;s date
                      will be used as the new submission date.
                    </p>

                    <div className={classes.confirmCard}>
                      <div className={classes.confirmCardTop}>
                        <div className={classes.confirmCardMid}>
                          <div className={classes.confirmCardName}>{providerName}</div>
                          <div className={classes.confirmCardMeta}>
                            {payerName} &middot; {batchClaims.length} client
                            {batchClaims.length !== 1 ? "s" : ""}
                          </div>
                        </div>
                        <div className={classes.confirmCardRight}>
                          <div className={classes.confirmCardId}>
                            {selectedClaim.batchId}
                          </div>
                          <div className={classes.confirmCardAmt}>
                            {fmtMoney(batchTotal)}
                          </div>
                        </div>
                      </div>
                      <div className={classes.confirmGrid}>
                        {[
                          ["Provider", providerName],
                          ["Payer", payerName],
                          ["Clients", String(batchClaims.length)],
                          ["Batch ID", selectedClaim.batchId || "—"],
                        ].map(([k, v]) => (
                          <div key={k}>
                            <div className={classes.confirmGridK}>{k}</div>
                            <div className={classes.confirmGridV}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className={classes.confirmDetails}>
                      <section>
                        <h6 className={classes.confirmSectionH6}>Provider</h6>
                        <dl className={classes.confirmKv}>
                          <dt className={classes.confirmKvDt}>Name</dt>
                          <dd className={classes.confirmKvDd}>{providerName}</dd>
                          <dt className={classes.confirmKvDt}>NPI / ID</dt>
                          <dd className={classes.confirmKvDd}>
                            {selectedClaim?.prov_npi || selectedClaim?.prov_id || selectedClaim?.bill_npi || selectedClaim?.bill_id || "—"}
                          </dd>
                          <dt className={classes.confirmKvDt}>Tax ID</dt>
                          <dd className={classes.confirmKvDd}>
                            {selectedClaim?.bill_taxid || "—"}
                          </dd>
                        </dl>
                      </section>

                      <section>
                        <h6 className={classes.confirmSectionH6}>Payer</h6>
                        <dl className={classes.confirmKv}>
                          <dt className={classes.confirmKvDt}>Name</dt>
                          <dd className={classes.confirmKvDd}>{payerName}</dd>
                          <dt className={classes.confirmKvDt}>Payer ID</dt>
                          <dd className={classes.confirmKvDd}>
                            {selectedClaim?.payerid || "—"}
                          </dd>
                          <dt className={classes.confirmKvDt}>Payer Order</dt>
                          <dd className={classes.confirmKvDd}>
                            {selectedClaim?.payer_order || "—"}
                          </dd>
                        </dl>
                      </section>

                      <section>
                        <h6 className={classes.confirmSectionH6}>
                          Clients ({batchClaims.length})
                        </h6>
                        <table className={classes.svcTable}>
                          <thead>
                            <tr>
                              <th>Client</th>
                              <th>DOB</th>
                              <th>Member ID</th>
                              <th>Claim ID</th>
                              <th className="num">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {batchClaims.map((c, i) => (
                              <tr key={i}>
                                <td>{patName(c)}</td>
                                <td>{c?.pat_dob ? fmtDate(c.pat_dob) : "—"}</td>
                                <td className={classes.monoCell}>
                                  {c?.ins_number || "—"}
                                </td>
                                <td className={classes.monoCell}>
                                  {c?.claimmd_id || `CLM-${i + 1}`}
                                </td>
                                <td className="num">{fmtMoney(claimTotal(c))}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </section>

                      <section>
                        <h6 className={classes.confirmSectionH6}>Service lines</h6>
                        <table className={classes.svcTable}>
                          <thead>
                            <tr>
                              <th>Date(s)</th>
                              <th>POS</th>
                              <th>Code &middot; Mod</th>
                              <th>Description</th>
                              <th className="num">Units</th>
                              <th className="num">Charge</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lines.length ? (
                              lines.map((l, i) => (
                                <tr key={i}>
                                  <td>
                                    {l?.from_date ? fmtDate(l.from_date) : "—"}
                                    {l?.thru_date && l.thru_date !== l.from_date
                                      ? ` – ${fmtDate(l.thru_date)}`
                                      : ""}
                                  </td>
                                  <td>{l?.place_of_service || "—"}</td>
                                  <td className={classes.monoCell}>
                                    {[l?.proc_code, l?.mod1]
                                      .filter(Boolean)
                                      .join(" · ") || "—"}
                                  </td>
                                  <td>{l?.description || "—"}</td>
                                  <td className="num">{l?.units || "—"}</td>
                                  <td className="num">
                                    {fmtMoney(num(l?.charge))}
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={6}>—</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                        <div className={classes.svcTotals}>
                          <div>
                            <span className={classes.svcTotalsK}>Total charge</span>
                            <span className={classes.svcTotalsV}>
                              {fmtMoney(batchTotal)}
                            </span>
                          </div>
                          <div>
                            <span className={classes.svcTotalsK}>Service period</span>
                            <span className={classes.svcTotalsV}>
                              {serviceFrom
                                ? `${fmtDate(serviceFrom)}${serviceTo ? ` – ${fmtDate(serviceTo)}` : ""}`
                                : "—"}
                            </span>
                          </div>
                        </div>
                      </section>

                      <section>
                        <h6 className={classes.confirmSectionH6}>Submission</h6>
                        <dl className={classes.confirmKv}>
                          <dt className={classes.confirmKvDt}>Submitted</dt>
                          <dd className={classes.confirmKvDd}>
                            {fmtDate(selectedBatch?.createdAt)}
                          </dd>
                          <dt className={classes.confirmKvDt}>Submission method</dt>
                          <dd className={classes.confirmKvDd}>
                            Clearinghouse (Claim MD)
                          </dd>
                        </dl>
                      </section>
                    </div>
                  </div>
                );
              }

              const name = patName(selectedClaim);
              const total = claimTotal(selectedClaim);
              const diagCodes =
                (selectedClaim?.diagnosis || []).map((d) => d?.code).filter(Boolean).join(", ") ||
                Object.keys(selectedClaim || {})
                  .filter((k) => /^diag_\d+$/.test(k))
                  .map((k) => selectedClaim[k])
                  .filter(Boolean)
                  .join(", ") ||
                "—";

              return (
                <div className={classes.confirmScroll}>
                  <p className={classes.confirmDesc}>
                    You're about to create a new claim by duplicating the one
                    below. The new claim will use the same provider, payer,
                    client, and service codes. Today's date will be used as the
                    new submission date.
                  </p>

                  <div className={classes.confirmCard}>
                    <div className={classes.confirmCardTop}>
                      <div className={classes.confirmCardAv}>
                        {getClientImage(selectedClaim, patients?.patients)
                          ? <img src={getClientImage(selectedClaim, patients?.patients)} alt={name} className={classes.confirmCardAvImg} />
                          : getInitials(name)
                        }
                      </div>
                      <div className={classes.confirmCardMid}>
                        <div className={classes.confirmCardName}>{name}</div>
                        <div className={classes.confirmCardMeta}>
                          {payerName} · {providerName}
                        </div>
                      </div>
                      <div className={classes.confirmCardRight}>
                        <div className={classes.confirmCardId}>
                          {selectedClaim?.claimmd_id || "—"}
                        </div>
                        <div className={classes.confirmCardAmt}>
                          {fmtMoney(total)}
                        </div>
                      </div>
                    </div>
                    <div className={classes.confirmGrid}>
                      {[
                        ["Provider", providerName],
                        ["Payer", payerName],
                        ["Source claim", selectedClaim?.claimmd_id || "—"],
                        ["Original status", statusLabel(selectedClaim?.status)],
                      ].map(([k, v]) => (
                        <div key={k}>
                          <div className={classes.confirmGridK}>{k}</div>
                          <div className={classes.confirmGridV}>{v}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={classes.confirmDetails}>
                    <section>
                      <h6 className={classes.confirmSectionH6}>Provider</h6>
                      <dl className={classes.confirmKv}>
                        <dt className={classes.confirmKvDt}>Name</dt>
                        <dd className={classes.confirmKvDd}>{providerName}</dd>
                        <dt className={classes.confirmKvDt}>NPI / ID</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.prov_npi || selectedClaim?.prov_id || selectedClaim?.bill_npi || selectedClaim?.bill_id || "—"}
                        </dd>
                        <dt className={classes.confirmKvDt}>Tax ID</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.bill_taxid || "—"}
                        </dd>
                        <dt className={classes.confirmKvDt}>Taxonomy</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.prov_taxonomy || "—"}
                        </dd>
                      </dl>
                    </section>

                    <section>
                      <h6 className={classes.confirmSectionH6}>Payer</h6>
                      <dl className={classes.confirmKv}>
                        <dt className={classes.confirmKvDt}>Name</dt>
                        <dd className={classes.confirmKvDd}>{payerName}</dd>
                        <dt className={classes.confirmKvDt}>Payer ID</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.payerid || "—"}
                        </dd>
                        <dt className={classes.confirmKvDt}>Payer Order</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.payer_order || "—"}
                        </dd>
                      </dl>
                    </section>

                    <section>
                      <h6 className={classes.confirmSectionH6}>Client</h6>
                      <dl className={classes.confirmKv}>
                        <dt className={classes.confirmKvDt}>Name</dt>
                        <dd className={classes.confirmKvDd}>{name}</dd>
                        <dt className={classes.confirmKvDt}>DOB</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.pat_dob
                            ? fmtDate(selectedClaim.pat_dob)
                            : "—"}
                        </dd>
                        <dt className={classes.confirmKvDt}>Member ID</dt>
                        <dd className={classes.confirmKvDd}>
                          {selectedClaim?.ins_number || "—"}
                        </dd>
                        <dt className={classes.confirmKvDt}>Address</dt>
                        <dd className={classes.confirmKvDd}>
                          {[
                            selectedClaim?.pat_addr_1,
                            selectedClaim?.pat_city,
                            selectedClaim?.pat_state,
                          ]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </dd>
                      </dl>
                    </section>

                    <section>
                      <h6 className={classes.confirmSectionH6}>
                        Diagnoses (ICD-10)
                      </h6>
                      <p className={classes.diagText}>{diagCodes}</p>
                    </section>

                    <section>
                      <h6 className={classes.confirmSectionH6}>
                        Service lines
                      </h6>
                      <table className={classes.svcTable}>
                        <thead>
                          <tr>
                            <th>Date(s)</th>
                            <th>POS</th>
                            <th>Code · Mod</th>
                            <th>Description</th>
                            <th className="num">Units</th>
                            <th className="num">Charge</th>
                          </tr>
                        </thead>
                        <tbody>
                          {lines.length ? (
                            lines.map((l, i) => (
                              <tr key={i}>
                                <td>
                                  {l?.from_date ? fmtDate(l.from_date) : "—"}
                                  {l?.thru_date && l.thru_date !== l.from_date
                                    ? ` – ${fmtDate(l.thru_date)}`
                                    : ""}
                                </td>
                                <td>{l?.place_of_service || "—"}</td>
                                <td className={classes.monoCell}>
                                  {[l?.proc_code, l?.mod1]
                                    .filter(Boolean)
                                    .join(" · ") || "—"}
                                </td>
                                <td>{l?.description || "—"}</td>
                                <td className="num">{l?.units || "—"}</td>
                                <td className="num">
                                  {fmtMoney(num(l?.charge))}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6}>—</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      <div className={classes.svcTotals}>
                        <div>
                          <span className={classes.svcTotalsK}>
                            Total charge
                          </span>
                          <span className={classes.svcTotalsV}>
                            {fmtMoney(total)}
                          </span>
                        </div>
                        <div>
                          <span className={classes.svcTotalsK}>
                            Service period
                          </span>
                          <span className={classes.svcTotalsV}>
                            {serviceFrom
                              ? `${fmtDate(serviceFrom)}${serviceTo ? ` – ${fmtDate(serviceTo)}` : ""}`
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </section>

                    <section>
                      <h6 className={classes.confirmSectionH6}>Submission</h6>
                      <dl className={classes.confirmKv}>
                        <dt className={classes.confirmKvDt}>Submitted</dt>
                        <dd className={classes.confirmKvDd}>
                          {fmtDate(selectedBatch?.createdAt)}
                        </dd>
                        <dt className={classes.confirmKvDt}>
                          Submission method
                        </dt>
                        <dd className={classes.confirmKvDd}>
                          Clearinghouse (Claim MD)
                        </dd>
                        <dt className={classes.confirmKvDt}>Status</dt>
                        <dd className={classes.confirmKvDd}>
                          {statusLabel(selectedClaim?.status)}
                        </dd>
                      </dl>
                    </section>
                  </div>
                </div>
              );
            })()}

        {/* foot */}
        <div className={classes.foot}>
          <button
            type="button"
            className={classes.ghostBtn}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={classes.accentBtn}
            disabled={!selectedClaim}
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default UsePreviousClaimDrawer;
